/**
 * Experience Mode Provider
 * 
 * Context provider for the Experience Mode tour system
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { User } from 'firebase/auth';
import { auth } from '../../firebase';
import { experienceModeService } from '../../services/experienceModeService';
import { ExperienceModeState, ExperienceSpot, TourProgress } from '../../types/experienceMode.types';

const HIDDEN_PAGES_KEY = 'experienceMode_hiddenPages';

interface ExperienceModeContextType {
  state: ExperienceModeState;
  activateExperienceMode: (pageId: string) => Promise<void>;
  deactivateExperienceMode: () => void;
  playVideo: (spot: ExperienceSpot) => void;
  closeVideo: () => void;
  markSpotAsSeen: (spotId: string) => Promise<void>;
  refreshProgress: () => Promise<void>;
  hiddenPages: string[];
  hidePageButton: (pageId: string) => void;
  keepPageButton: (pageId: string) => void;
  pageJustCompleted: string | null;
  dismissPageCompletion: () => void;
  isPageComplete: (pageId: string) => boolean;
}

const ExperienceModeContext = createContext<ExperienceModeContextType | null>(null);

export const ExperienceModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ExperienceModeState>({
    isActive: false,
    currentPageId: null,
    spots: [],
    activeSpot: null,
    isPlayingVideo: false,
    progress: null,
    isLoading: false,
  });
  
  // Per-page button hide preferences (localStorage)
  const [hiddenPages, setHiddenPages] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(HIDDEN_PAGES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  
  // Track when a page was just completed (to show completion card)
  const [pageJustCompleted, setPageJustCompleted] = useState<string | null>(null);
  
  // All spots cache (for page completion checks)
  const [allSpots, setAllSpots] = useState<ExperienceSpot[]>([]);

  // Persist hidden pages to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_PAGES_KEY, JSON.stringify(hiddenPages));
    } catch { /* ignore */ }
  }, [hiddenPages]);

  /**
   * Load user progress and all spots on mount and auth state changes
   */
  useEffect(() => {
    const loadProgress = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      try {
        const progress = await experienceModeService.getUserProgress(user.uid);
        setState(prev => ({ ...prev, progress }));
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    };
    
    const loadAllSpots = async () => {
      try {
        const spots = await experienceModeService.getAllSpots();
        setAllSpots(spots);
      } catch (error) {
        console.error('Error loading all spots:', error);
      }
    };
    
    loadProgress();
    loadAllSpots();
    
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      if (user) {
        loadProgress();
        loadAllSpots();
      } else {
        setState(prev => ({ ...prev, progress: null }));
      }
    });
    
    return () => unsubscribe();
  }, []);

  /**
   * Activate Experience Mode for a specific page
   */
  const activateExperienceMode = useCallback(async (pageId: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const spots = await experienceModeService.getSpotsForPage(pageId);
      
      setState(prev => ({
        ...prev,
        isActive: true,
        currentPageId: pageId,
        spots,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error activating experience mode:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  /**
   * Deactivate Experience Mode
   */
  const deactivateExperienceMode = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
      currentPageId: null,
      spots: [],
      activeSpot: null,
      isPlayingVideo: false,
    }));
  }, []);

  /**
   * Play video for a specific spot
   */
  const playVideo = useCallback((spot: ExperienceSpot) => {
    setState(prev => ({
      ...prev,
      activeSpot: spot,
      isPlayingVideo: true,
    }));
  }, []);

  /**
   * Close video player
   */
  const closeVideo = useCallback(() => {
    setState(prev => ({
      ...prev,
      activeSpot: null,
      isPlayingVideo: false,
    }));
  }, []);

  /**
   * Check if all spots for a page are seen
   */
  const isPageComplete = useCallback((pageId: string): boolean => {
    if (!state.progress) return false;
    const pageSpotsIds = allSpots
      .filter(s => s.pageId === pageId)
      .map(s => s.id);
    if (pageSpotsIds.length === 0) return false;
    return pageSpotsIds.every(id => state.progress!.seenSpots.includes(id));
  }, [allSpots, state.progress]);

  /**
   * Mark spot as seen and update progress, check page completion
   */
  const markSpotAsSeen = useCallback(async (spotId: string) => {
    const user = auth.currentUser;
    if (!user) return;
    
    try {
      // Get progress BEFORE marking to check if this spot was already seen
      const progressBefore = await experienceModeService.getUserProgress(user.uid);
      const wasAlreadySeen = progressBefore?.seenSpots.includes(spotId) ?? false;
      
      await experienceModeService.markSpotAsSeen(user.uid, spotId);
      await refreshProgress();
      
      // Only check page completion if this was a NEW spot being seen for the first time
      if (wasAlreadySeen) return;
      
      // After progress is refreshed, check if the current page is now complete
      const freshProgress = await experienceModeService.getUserProgress(user.uid);
      if (freshProgress && state.currentPageId) {
        const pageSpotsIds = allSpots
          .filter(s => s.pageId === state.currentPageId)
          .map(s => s.id);
        
        // Check: all spots on this page must be seen
        const allSeen = pageSpotsIds.length > 0 && 
          pageSpotsIds.every(id => freshProgress.seenSpots.includes(id));
        
        if (allSeen && !hiddenPages.includes(state.currentPageId)) {
          // All spots on this page are now seen — trigger completion card
          setPageJustCompleted(state.currentPageId);
        }
      }
    } catch (error) {
      console.error('Error marking spot as seen:', error);
    }
  }, [allSpots, state.currentPageId, hiddenPages]);

  /**
   * Refresh user progress from Firebase
   */
  const refreshProgress = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    
    try {
      const progress = await experienceModeService.getUserProgress(user.uid);
      setState(prev => ({ ...prev, progress }));
    } catch (error) {
      console.error('Error refreshing progress:', error);
    }
  }, []);

  /**
   * Hide the green tour button for a specific page
   */
  const hidePageButton = useCallback((pageId: string) => {
    setHiddenPages(prev => prev.includes(pageId) ? prev : [...prev, pageId]);
    setPageJustCompleted(null);
  }, []);

  /**
   * Keep the green tour button visible for a specific page
   */
  const keepPageButton = useCallback((pageId: string) => {
    // Just dismiss the card, don't add to hidden
    setPageJustCompleted(null);
  }, []);

  /**
   * Dismiss the page completion card
   */
  const dismissPageCompletion = useCallback(() => {
    setPageJustCompleted(null);
  }, []);

  return (
    <ExperienceModeContext.Provider
      value={{
        state,
        activateExperienceMode,
        deactivateExperienceMode,
        playVideo,
        closeVideo,
        markSpotAsSeen,
        refreshProgress,
        hiddenPages,
        hidePageButton,
        keepPageButton,
        pageJustCompleted,
        dismissPageCompletion,
        isPageComplete,
      }}
    >
      {children}
    </ExperienceModeContext.Provider>
  );
};

/**
 * Hook to use Experience Mode context
 */
export const useExperienceMode = () => {
  const context = useContext(ExperienceModeContext);
  if (!context) {
    throw new Error('useExperienceMode must be used within ExperienceModeProvider');
  }
  return context;
};
